import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  ArrowLeft, 
  Copy, 
  Check, 
  Download,
  Instagram,
  Facebook,
  Mail,
  MessageSquare,
  FileText,
  Sparkles,
  Home,
  ExternalLink,
  FileDown,
  Loader2,
  ImageDown,
  Share2,
  Link as LinkIcon,
  CopyPlus,
  Upload,
  Plus,
  X,
  Sofa,
  Palette,
  Maximize2,
  ChevronLeft,
  ChevronRight,
  Calculator,
  DollarSign,
  Trash2,
  User,
  Info,
  RefreshCw,
  GripVertical
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Safe number formatting helpers
const safeNum = (val) => { const n = Number(val); return isNaN(n) ? 0 : n; };
const fmtCurrency = (val) => safeNum(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtPct = (val, d = 1) => safeNum(val).toFixed(d);

const statusColors = {
  Draft: 'bg-gray-100 text-gray-700',
  Active: 'bg-green-100 text-green-700',
  Pending: 'bg-yellow-100 text-yellow-700',
  Sold: 'bg-blue-100 text-blue-700',
  Archived: 'bg-slate-100 text-slate-600'
};

// Sortable Photo Component for drag-and-drop reordering
const SortablePhoto = ({ photo, index, onClick, onDelete, isFirst, onStage, onEnhance, enhancingPhotoId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: photo.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.8 : 1,
  };

  const isEnhancing = enhancingPhotoId === photo.id;
  const canEnhance = !photo.enhanced_photo_url && !photo.staged_photo_url && !isEnhancing;
  const canStage = !photo.staged_photo_url;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`aspect-[4/3] rounded-xl overflow-hidden bg-muted relative group cursor-pointer ${isDragging ? 'ring-2 ring-primary shadow-lg' : ''}`}
      data-testid={`photo-card-${index}`}
    >
      <img
        src={photo.staged_photo_url || photo.enhanced_photo_url || photo.thumbnail_url || photo.original_photo_url}
        alt="Listing photo"
        className="w-full h-full object-cover"
        loading="lazy"
        onClick={() => onClick(photo)}
      />
      
      {/* First photo badge */}
      {isFirst && (
        <div className="absolute top-2 left-2 bg-blue-600 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1 z-10">
          ★ Main Photo
        </div>
      )}
      
      {/* Enhanced/Staged/Failed badges */}
      {photo.enhanced_photo_url && !photo.staged_photo_url && (
        <div className={`absolute top-2 ${isFirst ? 'right-2' : 'right-2'} bg-green-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1`}>
          <Sparkles size={10} />
          Enhanced
        </div>
      )}
      {photo.enhancement_failed && !photo.enhanced_photo_url && !photo.staged_photo_url && (
        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
          <X size={10} />
          Failed
        </div>
      )}
      {photo.staged_photo_url && (
        <div className={`absolute top-2 ${isFirst ? 'right-2' : 'right-2'} bg-purple-500 text-white px-2 py-0.5 rounded-full text-xs flex items-center gap-1`}>
          <Sofa size={10} />
          Staged
        </div>
      )}
      
      {/* Enhancing indicator */}
      {isEnhancing && (
        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
          <div className="text-white text-center">
            <Loader2 size={24} className="animate-spin mx-auto mb-1" />
            <span className="text-xs">Enhancing...</span>
          </div>
        </div>
      )}
      
      {/* Hover actions */}
      {!isEnhancing && (
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
          {/* Top row - Main actions */}
          <div className="flex items-center gap-2">
            {/* Drag handle */}
            <button
              {...attributes}
              {...listeners}
              className="bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full cursor-grab active:cursor-grabbing"
              title="Drag to reorder"
              data-testid={`drag-handle-${index}`}
            >
              <GripVertical size={16} />
            </button>
            
            {/* Expand button */}
            <button
              onClick={() => onClick(photo)}
              className="bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full"
              title="View full size"
              data-testid={`expand-photo-${index}`}
            >
              <Maximize2 size={16} />
            </button>
            
            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(photo.id);
              }}
              className="bg-red-500/90 hover:bg-red-500 text-white p-2 rounded-full"
              title="Delete photo"
              data-testid={`delete-photo-${index}`}
            >
              <Trash2 size={16} />
            </button>
          </div>
          
          {/* Bottom row - Enhancement actions */}
          <div className="flex items-center gap-2">
            {/* Enhance button */}
            {canEnhance && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEnhance(photo.id);
                }}
                className="bg-green-500/90 hover:bg-green-500 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1"
                title="Enhance this photo"
                data-testid={`enhance-photo-${index}`}
              >
                <Sparkles size={12} />
                Enhance
              </button>
            )}
            
            {/* Virtual Staging button */}
            {canStage && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onStage(photo);
                }}
                className="bg-purple-500/90 hover:bg-purple-500 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-1"
                title="Virtual staging"
                data-testid={`stage-photo-${index}`}
              >
                <Sofa size={12} />
                Stage
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ListingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [content, setContent] = useState([]);
  const [selectedLang, setSelectedLang] = useState('EN');
  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [enhancingAll, setEnhancingAll] = useState(false);
  const [downloadingPhotos, setDownloadingPhotos] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [sharingLoading, setSharingLoading] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [socialImages, setSocialImages] = useState([]);
  const [generatingSocialImages, setGeneratingSocialImages] = useState(false);
  const [selectedPhotosForSocial, setSelectedPhotosForSocial] = useState([]);
  const [agentProfile, setAgentProfile] = useState(null);
  
  // Branded Social Image State
  const [generatingBrandedImage, setGeneratingBrandedImage] = useState(false);
  const [brandedImage, setBrandedImage] = useState(null);
  const [selectedPhotoForBranded, setSelectedPhotoForBranded] = useState(null);
  const [includeAgentPhoto, setIncludeAgentPhoto] = useState(true);
  const [includeBrokerLogo, setIncludeBrokerLogo] = useState(true);
  
  // Virtual Staging State
  const [showStagingModal, setShowStagingModal] = useState(false);
  const [stagingPhotos, setStagingPhotos] = useState([]); // Changed to array for multi-select
  const [stagingType, setStagingType] = useState('furniture');
  const [stagingStyle, setStagingStyle] = useState('modern');
  const [stagingRoom, setStagingRoom] = useState('living_room');
  const [stagingInProgress, setStagingInProgress] = useState(false);
  const [stagingProgress, setStagingProgress] = useState({ current: 0, total: 0 });
  const [consistentRoomMode, setConsistentRoomMode] = useState(false); // Same room, different angles
  
  // Photo Lightbox State
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  
  // Enhancement Level State
  const [enhancementLevel, setEnhancementLevel] = useState('standard');
  const [refreshing, setRefreshing] = useState(false);
  const [isDraggingPhotos, setIsDraggingPhotos] = useState(false);
  const [isReordering, setIsReordering] = useState(false);
  const [enhancingPhotoId, setEnhancingPhotoId] = useState(null);

  // DnD sensors for photo reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle photo reorder via drag and drop
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const photos = listing?.photos || [];
    const oldIndex = photos.findIndex(p => p.id === active.id);
    const newIndex = photos.findIndex(p => p.id === over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    // Optimistically update the UI
    const newPhotos = arrayMove(photos, oldIndex, newIndex);
    setListing(prev => ({ ...prev, photos: newPhotos }));
    
    // Save to backend
    setIsReordering(true);
    try {
      await axios.put(
        `${API}/listings/${id}/photos/reorder`,
        { photo_ids: newPhotos.map(p => p.id) },
        { headers: getAuthHeaders() }
      );
      toast.success('Photos reordered! First photo is now your main image.');
    } catch (error) {
      console.error('Reorder error:', error);
      toast.error('Failed to save photo order');
      // Revert on error
      fetchListing();
    } finally {
      setIsReordering(false);
    }
  };

  // Drag and drop handlers for file uploads
  const handlePhotoDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhotos(true);
  };

  const handlePhotoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingPhotos(false);
  };

  const handlePhotoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Helper function to get all files from a directory entry recursively
  const getFilesFromDirectory = async (dirEntry) => {
    const files = [];
    const reader = dirEntry.createReader();
    
    const readEntries = () => new Promise((resolve, reject) => {
      reader.readEntries(resolve, reject);
    });
    
    let entries = await readEntries();
    while (entries.length > 0) {
      for (const entry of entries) {
        if (entry.isFile) {
          const file = await new Promise((resolve) => entry.file(resolve));
          if (file.type.startsWith('image/') || 
              file.name.toLowerCase().endsWith('.heic') ||
              file.name.toLowerCase().endsWith('.dng') ||
              file.name.toLowerCase().endsWith('.raw')) {
            files.push(file);
          }
        } else if (entry.isDirectory) {
          const subFiles = await getFilesFromDirectory(entry);
          files.push(...subFiles);
        }
      }
      entries = await readEntries();
    }
    return files;
  };

  const handlePhotoDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingPhotos(false);
    
    const items = e.dataTransfer.items;
    const files = [];
    const processPromises = [];
    
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry?.();
        if (entry) {
          if (entry.isDirectory) {
            processPromises.push(getFilesFromDirectory(entry));
          } else {
            const file = item.getAsFile();
            if (file && (file.type.startsWith('image/') || 
                file.name.toLowerCase().endsWith('.heic') ||
                file.name.toLowerCase().endsWith('.dng') ||
                file.name.toLowerCase().endsWith('.raw'))) {
              files.push(file);
            }
          }
        } else {
          const file = item.getAsFile();
          if (file && file.type.startsWith('image/')) {
            files.push(file);
          }
        }
      }
    }
    
    const directoryFiles = await Promise.all(processPromises);
    directoryFiles.forEach(dirFiles => files.push(...dirFiles));
    
    if (files.length === 0) {
      toast.error('No valid image files found');
      return;
    }
    
    toast.info(`Found ${files.length} image${files.length > 1 ? 's' : ''} to upload`);
    
    const fakeEvent = { target: { files } };
    handlePhotoUpload(fakeEvent);
  };

  // Refresh handler with visual feedback
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchListing();
    setRefreshing(false);
    toast.success('Photos refreshed!');
  };

  useEffect(() => {
    fetchListing();
  }, [id]);

  // Auto-generate content if listing loaded but no content exists
  const autoGenAttempted = useRef(false);
  useEffect(() => {
    if (!listing || loading || generatingContent) return;
    if (content.length > 0) return;
    if (autoGenAttempted.current) return;
    if (!listing.address) return;
    
    autoGenAttempted.current = true;
    
    const autoGenerate = async () => {
      setGeneratingContent(true);
      toast.loading('Generating marketing content...', { id: 'auto-gen-content', description: 'This may take up to 60 seconds.' });
      try {
        await axios.post(
          `${API}/listings/${id}/generate`,
          {},
          { headers: getAuthHeaders(), timeout: 120000 }
        );
        toast.success('Content generated!', { id: 'auto-gen-content' });
        fetchListing();
      } catch (err) {
        toast.error('Content generation failed. Use "Regenerate" to retry.', { id: 'auto-gen-content' });
      } finally {
        setGeneratingContent(false);
      }
    };
    
    autoGenerate();
  }, [listing, loading]);

  const handleVirtualStage = async () => {
    if (stagingPhotos.length === 0) return;
    
    setStagingInProgress(true);
    setStagingProgress({ current: 0, total: stagingPhotos.length });
    
    // Show loading toast
    const toastId = toast.loading('Virtual staging in progress...', {
      description: 'AI is adding furniture to your photos. This may take 30-60 seconds per photo.'
    });
    
    try {
      // Check if using consistent room mode (same furniture across angles)
      if (consistentRoomMode && stagingPhotos.length >= 2) {
        // Use the consistent staging API for same room, different angles
        const response = await axios.post(
          `${API}/listings/${id}/photos/consistent-stage`,
          {
            photo_ids: stagingPhotos.map(p => p.id),
            room_type: stagingRoom,
            style: stagingStyle
          },
          { headers: getAuthHeaders(), timeout: 300000 }
        );
        
        toast.dismiss(toastId);
        
        const successCount = response.data.results?.length || 0;
        const errorCount = response.data.errors?.length || 0;
        
        if (successCount > 0) {
          toast.success(`Staged ${successCount} photos with consistent furniture!`, {
            description: `Same ${stagingRoom.replace('_', ' ')} furniture applied to all angles.`
          });
        }
        if (errorCount > 0) {
          toast.warning(`${errorCount} photo(s) could not be staged.`, {
            description: 'Try staging these individually.'
          });
        }
      } else if (stagingPhotos.length === 1) {
        // Single photo staging
        await axios.post(
          `${API}/listings/${id}/photos/${stagingPhotos[0].id}/virtual-stage`,
          {
            staging_type: stagingType,
            style: stagingType === 'style' ? stagingStyle : null,
            room_type: stagingRoom
          },
          { headers: getAuthHeaders(), timeout: 180000 }
        );
        toast.dismiss(toastId);
        toast.success('Virtual staging completed!', {
          description: 'Your photo has been virtually staged.'
        });
      } else {
        // Batch staging - process one at a time for better UX feedback
        let completed = 0;
        let failed = 0;
        for (const photo of stagingPhotos) {
          try {
            await axios.post(
              `${API}/listings/${id}/photos/${photo.id}/virtual-stage`,
              {
                staging_type: stagingType,
                style: stagingType === 'style' ? stagingStyle : null,
                room_type: stagingRoom
              },
              { headers: getAuthHeaders(), timeout: 180000 }
            );
            completed++;
            setStagingProgress({ current: completed, total: stagingPhotos.length });
          } catch (err) {
            console.error(`Failed to stage photo ${photo.id}:`, err);
            failed++;
          }
        }
        toast.dismiss(toastId);
        if (completed > 0) {
          toast.success(`Staged ${completed} of ${stagingPhotos.length} photos!`, {
            description: failed > 0 ? `${failed} photo(s) failed - try again later.` : undefined
          });
        } else {
          toast.error('All staging attempts failed', {
            description: 'Please try again later or contact support.'
          });
        }
      }
      
      setShowStagingModal(false);
      setStagingPhotos([]);
      setConsistentRoomMode(false);
      fetchListing(); // Refresh to show staged photos
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Staging error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Staging is taking longer than expected', {
          description: 'The AI may still be processing. Refresh the page in 1-2 minutes.',
          duration: 10000
        });
      } else {
        toast.error('Virtual staging failed', {
          description: error.response?.data?.detail || 'Please try again later.'
        });
      }
    } finally {
      setStagingInProgress(false);
      setStagingProgress({ current: 0, total: 0 });
    }
  };

  const openStagingModal = (photo) => {
    setStagingPhotos([photo]);
    setShowStagingModal(true);
  };
  
  const openBatchStagingModal = () => {
    // Open modal with all unstaged photos selected
    const unstagedPhotos = listing?.photos?.filter(p => !p.staged_photo_url) || [];
    setStagingPhotos(unstagedPhotos);
    setShowStagingModal(true);
  };
  
  const togglePhotoForStaging = (photo) => {
    setStagingPhotos(prev => {
      const isSelected = prev.some(p => p.id === photo.id);
      if (isSelected) {
        return prev.filter(p => p.id !== photo.id);
      } else {
        return [...prev, photo];
      }
    });
  };
  
  const openLightbox = (photo) => {
    setShowLightbox(true);
    setLightboxPhoto(photo);
  };
  
  const navigateLightbox = (direction) => {
    if (!listing?.photos) return;
    const currentIndex = listing.photos.findIndex(p => p.id === lightboxPhoto?.id);
    const newIndex = direction === 'prev' 
      ? (currentIndex - 1 + listing.photos.length) % listing.photos.length
      : (currentIndex + 1) % listing.photos.length;
    setLightboxPhoto(listing.photos[newIndex]);
  };

  const fetchListing = async (retryCount = 0) => {
    try {
      // Fetch listing (includes photos and generated_content) and user profile in parallel
      const [listingRes, profileRes] = await Promise.all([
        axios.get(`${API}/listings/${id}`, { 
          headers: getAuthHeaders(),
          timeout: 30000 // 30 second timeout
        }),
        axios.get(`${API}/auth/me`, { 
          headers: getAuthHeaders(),
          timeout: 30000
        })
      ]);
      
      setListing(listingRes.data);
      setAgentProfile(profileRes.data);
      
      // Check if there are photos that still need enhancement and notify user
      const photos = listingRes.data.photos || [];
      const unenhancedCount = photos.filter(p => 
        !p.enhanced_photo_url || p.enhanced_photo_url === p.original_photo_url
      ).length;
      
      if (unenhancedCount > 0 && photos.length > 0) {
        // Only show this toast if user has been on the page (not on initial load)
        if (!loading) {
          toast.info(`${unenhancedCount} photo${unenhancedCount > 1 ? 's' : ''} still need enhancement`, {
            description: 'Click "Enhance All" to continue processing.',
            duration: 8000
          });
        }
      }
      
      // Content is already included in listing response as generated_content
      const generatedContent = listingRes.data.generated_content;
      if (generatedContent) {
        // Wrap in array format for compatibility with existing UI
        setContent([generatedContent]);
        setSelectedLang(generatedContent.language || 'English');
      } else {
        setContent([]);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      // Retry up to 2 times on failure
      if (retryCount < 2) {
        console.log(`Retrying listing fetch (attempt ${retryCount + 2})...`);
        setTimeout(() => fetchListing(retryCount + 1), 1000);
        return;
      }
      toast.error('Failed to load listing. Please try again.');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  // Generate professional HTML email template
  const generateEmailHTML = () => {
    const photoUrl = currentContent?.photo_url || listing?.photos?.[0]?.staged_photo_url || listing?.photos?.[0]?.enhanced_photo_url || listing?.photos?.[0]?.original_photo_url;
    const price = listing?.price ? `$${listing.price.toLocaleString()}` : 'Contact for Price';
    const agent = agentProfile || user;
    
    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: Arial, Helvetica, sans-serif; background-color: #f5f5f5;">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="max-width: 600px; margin: 0 auto; background-color: #ffffff;">
    <!-- Header with Property Image -->
    <tr>
      <td style="padding: 0;">
        ${photoUrl ? `<img src="${photoUrl}" alt="${listing?.address || 'Property'}" style="width: 100%; height: auto; display: block; border-radius: 0;">` : ''}
      </td>
    </tr>
    
    <!-- Property Details -->
    <tr>
      <td style="padding: 24px;">
        <!-- Status Badge -->
        <table role="presentation" cellspacing="0" cellpadding="0" style="margin-bottom: 16px;">
          <tr>
            <td style="background-color: ${listing?.status === 'Active' ? '#22c55e' : listing?.status === 'Pending' ? '#eab308' : '#3b82f6'}; color: white; padding: 6px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: uppercase;">
              ${listing?.status || 'Active'}
            </td>
          </tr>
        </table>
        
        <!-- Price -->
        <h1 style="margin: 0 0 8px 0; font-size: 32px; font-weight: 700; color: #1a1a1a;">${price}</h1>
        
        <!-- Address -->
        <p style="margin: 0 0 16px 0; font-size: 18px; color: #4a4a4a;">${listing?.address || ''}</p>
        
        <!-- Property Features -->
        <table role="presentation" cellspacing="0" cellpadding="0" style="margin-bottom: 20px;">
          <tr>
            <td style="padding-right: 24px;">
              <span style="font-size: 24px; font-weight: 700; color: #1a1a1a;">${listing?.bedrooms || 0}</span>
              <span style="font-size: 14px; color: #6b7280; margin-left: 4px;">Beds</span>
            </td>
            <td style="padding-right: 24px;">
              <span style="font-size: 24px; font-weight: 700; color: #1a1a1a;">${listing?.bathrooms || 0}</span>
              <span style="font-size: 14px; color: #6b7280; margin-left: 4px;">Baths</span>
            </td>
            <td>
              <span style="font-size: 24px; font-weight: 700; color: #1a1a1a;">${listing?.sqft?.toLocaleString() || 0}</span>
              <span style="font-size: 14px; color: #6b7280; margin-left: 4px;">Sq Ft</span>
            </td>
          </tr>
        </table>
        
        <!-- Divider -->
        <hr style="border: none; border-top: 1px solid #e5e7eb; margin: 20px 0;">
        
        <!-- Description/Remarks -->
        <h2 style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">Property Description</h2>
        <p style="margin: 0 0 24px 0; font-size: 15px; line-height: 1.6; color: #4a4a4a;">
          ${currentContent?.mls_remarks || currentContent?.email_copy_body || 'Contact agent for more details about this beautiful property.'}
        </p>
        
        <!-- Key Features if available -->
        ${Array.isArray(listing?.key_features) && listing.key_features.length > 0 ? `
        <h3 style="margin: 0 0 12px 0; font-size: 16px; font-weight: 600; color: #1a1a1a;">Key Features</h3>
        <ul style="margin: 0 0 24px 0; padding-left: 20px; color: #4a4a4a; font-size: 14px; line-height: 1.8;">
          ${listing.key_features.slice(0, 5).map(f => `<li>${f}</li>`).join('')}
        </ul>
        ` : ''}
      </td>
    </tr>
    
    <!-- Agent Card -->
    <tr>
      <td style="padding: 0 24px 24px 24px;">
        <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: #f8fafc; border-radius: 12px;">
          <tr>
            <td style="padding: 24px; text-align: center;">
              <!-- Agent Photo on Top - Using width/height attributes for Gmail compatibility -->
              ${agent?.headshot_url ? `
              <table role="presentation" cellspacing="0" cellpadding="0" style="margin: 0 auto 12px auto;">
                <tr>
                  <td>
                    <img src="${agent.headshot_url}" alt="${agent.name || 'Agent'}" width="80" height="80" style="border-radius: 50%; object-fit: cover; border: 3px solid #ffffff; display: block;">
                  </td>
                </tr>
              </table>
              ` : ''}
              
              <!-- Agent Name & Title -->
              <p style="margin: 0 0 4px 0; font-size: 18px; font-weight: 600; color: #1a1a1a;">${agent?.name || 'Real Estate Agent'}</p>
              ${agent?.title ? `<p style="margin: 0 0 4px 0; font-size: 14px; color: #6b7280;">${agent.title}</p>` : ''}
              ${agent?.brokerage_name ? `<p style="margin: 0 0 8px 0; font-size: 14px; color: #6b7280;">${agent.brokerage_name}</p>` : ''}
              
              <!-- Tiny Brokerage Logo - Using width/height attributes for Gmail -->
              ${agent?.brokerage_logo ? `
              <table role="presentation" cellspacing="0" cellpadding="0" style="margin: 0 auto 12px auto;">
                <tr>
                  <td>
                    <img src="${agent.brokerage_logo}" alt="Brokerage" width="35" height="18" style="display: block;">
                  </td>
                </tr>
              </table>
              ` : ''}
              
              <!-- Contact Info -->
              <table role="presentation" cellspacing="0" cellpadding="0" style="margin: 0 auto;">
                ${agent?.phone ? `
                <tr>
                  <td style="padding-bottom: 4px; text-align: center;">
                    <a href="tel:${agent.phone}" style="color: #2563eb; text-decoration: none; font-size: 14px;">📞 ${agent.phone}</a>
                  </td>
                </tr>
                ` : ''}
                ${agent?.email ? `
                <tr>
                  <td style="padding-bottom: 4px; text-align: center;">
                    <a href="mailto:${agent.email}" style="color: #2563eb; text-decoration: none; font-size: 14px;">✉️ ${agent.email}</a>
                  </td>
                </tr>
                ` : ''}
                ${agent?.license_number ? `
                <tr>
                  <td style="text-align: center;">
                    <span style="font-size: 11px; color: #9ca3af;">License: ${agent.license_number}</span>
                  </td>
                </tr>
                ` : ''}
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
    
    <!-- Footer -->
    <tr>
      <td style="padding: 20px 24px; background-color: #f8fafc; text-align: center;">
        <p style="margin: 0; font-size: 12px; color: #9ca3af;">
          © ${new Date().getFullYear()} ${agent?.brokerage_name || 'Real Estate'}. All rights reserved.
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const copyEmailHTML = async () => {
    const html = generateEmailHTML();
    try {
      // Create a blob with HTML content
      const blob = new Blob([html], { type: 'text/html' });
      const richTextBlob = new Blob([html], { type: 'text/html' });
      const plainTextBlob = new Blob([html], { type: 'text/plain' });
      
      // Use ClipboardItem to copy as rich text
      const clipboardItem = new ClipboardItem({
        'text/html': richTextBlob,
        'text/plain': plainTextBlob
      });
      
      await navigator.clipboard.write([clipboardItem]);
      setCopiedField('email_html');
      toast.success('Email copied! Paste it in your email editor.');
      setTimeout(() => setCopiedField(null), 3000);
    } catch (error) {
      // Fallback for browsers that don't support ClipboardItem
      console.error('Rich copy failed, trying fallback:', error);
      try {
        // Alternative: Create a temporary element and copy from it
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        tempDiv.style.position = 'fixed';
        tempDiv.style.left = '-9999px';
        document.body.appendChild(tempDiv);
        
        const range = document.createRange();
        range.selectNodeContents(tempDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);
        
        document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(tempDiv);
        
        setCopiedField('email_html');
        toast.success('Email copied! Paste it in your email editor.');
        setTimeout(() => setCopiedField(null), 3000);
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
        toast.error('Failed to copy. Try selecting and copying manually.');
      }
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(
        `${API}/listings/${id}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      setListing({ ...listing, status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Status update error:', error);
      toast.error('Failed to update status');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(
        `${API}/listings/${id}/export`,
        { headers: getAuthHeaders() }
      );
      
      // Create downloadable JSON
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `listing-${id}-export.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('Export downloaded!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export');
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await axios.get(
        `${API}/listings/${id}/export/pdf`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `listing-${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded!');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPhotos = async () => {
    if (!listing?.photos || listing.photos.length === 0) {
      toast.error('No photos to download');
      return;
    }

    setDownloadingPhotos(true);
    
    try {
      // Start background download preparation
      const startResponse = await axios.post(
        `${API}/listings/${id}/photos/prepare-download`,
        {},
        { headers: getAuthHeaders() }
      );
      
      const { job_id, photo_count } = startResponse.data;
      toast.info(`Preparing ${photo_count} photos for download...`, { duration: 5000 });
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 2 minutes max (1 second intervals)
      
      const pollStatus = async () => {
        attempts++;
        
        try {
          const statusResponse = await axios.get(
            `${API}/listings/${id}/photos/download-status/${job_id}`,
            { headers: getAuthHeaders() }
          );
          
          const { status, progress, download_url, error, downloaded_count, photo_count: total } = statusResponse.data;
          
          if (status === 'completed' && download_url) {
            // Download the ZIP file
            const a = document.createElement('a');
            a.href = download_url;
            a.download = `listing-${id.slice(0, 8)}-photos.zip`;
            a.click();
            
            toast.success(`Downloaded ${downloaded_count} photos!`);
            setDownloadingPhotos(false);
            return;
          }
          
          if (status === 'failed') {
            toast.error(error || 'Download preparation failed');
            setDownloadingPhotos(false);
            return;
          }
          
          if (attempts >= maxAttempts) {
            toast.error('Download timed out. Please try again.');
            setDownloadingPhotos(false);
            return;
          }
          
          // Still processing, show progress and continue polling
          if (progress > 0 && attempts % 5 === 0) {
            toast.info(`Preparing: ${progress}% (${downloaded_count || 0}/${total} photos)`, { duration: 2000 });
          }
          
          // Poll again after 1 second
          setTimeout(pollStatus, 1000);
          
        } catch (pollError) {
          console.error('Poll error:', pollError);
          if (attempts >= maxAttempts) {
            toast.error('Download failed. Please try again.');
            setDownloadingPhotos(false);
          } else {
            setTimeout(pollStatus, 1000);
          }
        }
      };
      
      // Start polling
      pollStatus();
      
    } catch (error) {
      console.error('Photos download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start download. Please try again.');
      setDownloadingPhotos(false);
    }
  };

  const handleEnhanceAllPhotos = async () => {
    const unenhancedPhotos = listing?.photos?.filter(p => !p.enhanced_photo_url && !p.enhancement_failed) || [];
    if (unenhancedPhotos.length === 0) {
      toast.info('All photos are already enhanced or failed', {
        description: 'Try enhancing individual photos that failed.'
      });
      return;
    }

    setEnhancingAll(true);
    
    const toastId = toast.loading(`Enhancing ${unenhancedPhotos.length} photos...`, {
      description: 'This runs in the background. You can continue working.'
    });
    
    try {
      const response = await axios.post(
        `${API}/listings/${id}/photos/enhance-all`,
        { level: enhancementLevel },
        { headers: getAuthHeaders(), timeout: 30000 }
      );
      
      toast.dismiss(toastId);
      
      // Handle the async response
      if (response.data.status === 'processing') {
        const levelNames = { subtle: 'Subtle', standard: 'Standard', dramatic: 'Dramatic' };
        toast.success(`${levelNames[enhancementLevel]} enhancement started!`, {
          description: `Processing ${response.data.queued_count} photos. Refresh in a few minutes and click "Enhance All" again if needed.`,
          duration: 15000
        });
      } else {
        toast.success(response.data.message || 'Enhancement complete!');
        fetchListing();
      }
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Batch enhance error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.info('Enhancement is processing in background', {
          description: 'This is normal for many photos. Refresh the page in 2-3 minutes.',
          duration: 10000
        });
      } else {
        const msg = error.response?.data?.detail || error.response?.data?.message || 'Failed to start enhancement. Please try again.';
        toast.error('Enhancement failed', { description: msg });
      }
    } finally {
      setEnhancingAll(false);
    }
  };

  // Handle single photo enhancement with better error handling
  const handleEnhanceSinglePhoto = async (photoId) => {
    setEnhancingPhotoId(photoId);
    
    // Show initial toast that won't auto-dismiss
    const toastId = toast.loading('Enhancing photo... This may take 15-30 seconds.', {
      description: 'AI is analyzing and improving your photo.'
    });
    
    try {
      const response = await axios.post(
        `${API}/listings/${id}/photos/${photoId}/enhance`,
        { level: enhancementLevel },
        { headers: getAuthHeaders(), timeout: 120000 } // 2 minute timeout
      );
      
      toast.dismiss(toastId);
      
      if (response.data.enhanced) {
        toast.success('Photo enhanced successfully!', {
          description: 'Your photo has been professionally enhanced.'
        });
      } else {
        toast.warning('Enhancement incomplete', {
          description: response.data.message || 'The AI could not enhance this photo. Try a different photo or try again later.'
        });
      }
      
      fetchListing(); // Refresh to get enhanced photo
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Single photo enhance error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Enhancement is taking longer than expected', {
          description: 'The AI is still processing. Please refresh the page in 30 seconds to see the result.',
          duration: 10000
        });
      } else {
        const msg = error.response?.data?.detail || 'Failed to enhance photo. Please try again.';
        toast.error('Enhancement failed', { description: msg });
      }
    } finally {
      setEnhancingPhotoId(null);
    }
  };

  // Handle opening staging modal for a single photo
  const handleStageSinglePhoto = (photo) => {
    setStagingPhotos([photo]);
    setShowStagingModal(true);
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingPhoto(true);
    let uploadedCount = 0;

    // Compression options - backend resizes to 1400px, so no need to send large files
    const compressionOptions = {
      maxSizeMB: 1.5,
      maxWidthOrHeight: 2048,
      useWebWorker: true,
      fileType: 'image/jpeg',
      initialQuality: 0.8
    };

    const processingToast = toast.loading(`Processing ${files.length} photo${files.length > 1 ? 's' : ''}...`);

    // Process all files first (compress/convert)
    const processedFiles = [];
    for (const file of files) {
      const fileSizeMB = file.size / (1024 * 1024);
      const fileName = file.name.toLowerCase();
      const isRawFile = fileName.endsWith('.dng') || fileName.endsWith('.raw') || fileName.endsWith('.cr2') || fileName.endsWith('.nef');

      if (isRawFile) {
        if (fileSizeMB > 100) {
          toast.error(`${file.name} is too large (${fileSizeMB.toFixed(0)}MB). Max 100MB for RAW files.`);
          continue;
        }
        // Show processing message for large RAW files
        if (fileSizeMB > 15) {
          toast.info(`${file.name} is ${fileSizeMB.toFixed(0)}MB - this may take a moment to process`, { duration: 5000 });
        }
        processedFiles.push(file);
        continue;
      }

      const needsCompression = fileSizeMB > 1.5 || fileName.endsWith('.heic');

      try {
        if (needsCompression) {
          const compressed = await imageCompression(file, compressionOptions);
          console.log(`Compressed ${file.name}: ${fileSizeMB.toFixed(1)}MB → ${(compressed.size / (1024 * 1024)).toFixed(1)}MB`);
          processedFiles.push(compressed);
        } else {
          processedFiles.push(file);
        }
      } catch (err) {
        console.error('Compression error:', err);
        toast.error(`Could not process ${file.name}. Try JPG or PNG format.`);
      }
    }

    toast.dismiss(processingToast);

    if (processedFiles.length === 0) {
      setUploadingPhoto(false);
      return;
    }

    const uploadingToast = toast.loading(`Uploading ${processedFiles.length} photo${processedFiles.length > 1 ? 's' : ''}...`);

    // Use batch endpoint for faster uploads (processes all in parallel on server)
    const batchSize = 10; // Server handles up to 20, but 10 is safer for network
    
    for (let i = 0; i < processedFiles.length; i += batchSize) {
      const batch = processedFiles.slice(i, i + batchSize);
      const formData = new FormData();
      batch.forEach(file => formData.append('files', file));
      
      try {
        // Check if batch contains RAW files (need longer timeout)
        const hasRawFiles = batch.some(f => {
          const name = f.name?.toLowerCase() || '';
          return name.endsWith('.dng') || name.endsWith('.raw') || name.endsWith('.cr2') || name.endsWith('.nef');
        });
        
        const response = await axios.post(
          `${API}/listings/${id}/photos/batch`,
          formData,
          { 
            headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
            timeout: hasRawFiles ? 300000 : 180000 // 5 min for RAW, 3 min for regular
          }
        );
        uploadedCount += response.data.uploaded?.length || 0;
        if (response.data.failed?.length > 0) {
          response.data.failed.forEach(f => toast.error(`Failed: ${f.filename || 'unknown'}`));
        }
      } catch (err) {
        const msg = err.response?.data?.detail || 'Batch upload failed';
        toast.error(msg);
      }
    }

    toast.dismiss(uploadingToast);
    
    setUploadingPhoto(false);
    e.target.value = ''; // Reset input

    if (uploadedCount > 0) {
      toast.success(`Uploaded ${uploadedCount} photo${uploadedCount > 1 ? 's' : ''}!`);
      fetchListing(); // Refresh to show new photos
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm('Are you sure you want to delete this photo?')) return;

    try {
      await axios.delete(
        `${API}/listings/${id}/photos/${photoId}`,
        { headers: getAuthHeaders() }
      );
      toast.success('Photo deleted');
      fetchListing(); // Refresh
    } catch (error) {
      console.error('Delete photo error:', error);
      toast.error('Failed to delete photo');
    }
  };

  const handleGenerateSocialImages = async (platform) => {
    if (selectedPhotosForSocial.length === 0) {
      toast.error('Please select at least one photo');
      return;
    }
    
    setGeneratingSocialImages(true);
    setSocialImages([]);
    
    const toastId = toast.loading(`Creating ${platform} images...`, {
      description: 'AI is generating your social media images.'
    });
    
    try {
      const response = await axios.post(
        `${API}/listings/${id}/social-images?platform=${platform}`,
        { photo_ids: selectedPhotosForSocial },
        { headers: getAuthHeaders(), timeout: 180000 }
      );
      
      toast.dismiss(toastId);
      setSocialImages(response.data.images);
      toast.success(`Generated ${response.data.images.length} ${platform} image${response.data.images.length > 1 ? 's' : ''}!`);
    } catch (error) {
      toast.dismiss(toastId);
      console.error('Generate social images error:', error);
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.error('Generation taking too long', {
          description: 'Try selecting fewer photos or try again later.',
          duration: 8000
        });
      } else {
        const message = error.response?.data?.detail || 'Failed to generate social media images';
        toast.error('Generation failed', { description: message });
      }
    } finally {
      setGeneratingSocialImages(false);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotosForSocial(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const selectAllPhotos = () => {
    if (listing?.photos?.length) {
      setSelectedPhotosForSocial(listing.photos.map(p => p.id));
    }
  };

  const deselectAllPhotos = () => {
    setSelectedPhotosForSocial([]);
  };

  // Generate Branded Social Image with agent photo and broker logo
  const handleGenerateBrandedImage = async () => {
    if (!selectedPhotoForBranded) {
      toast.error('Please select a photo');
      return;
    }
    
    setGeneratingBrandedImage(true);
    setBrandedImage(null);
    
    try {
      const response = await axios.post(
        `${API}/listings/${id}/branded-social-image`,
        { 
          photo_id: selectedPhotoForBranded,
          include_agent_photo: includeAgentPhoto,
          include_broker_logo: includeBrokerLogo
        },
        { headers: getAuthHeaders() }
      );
      
      setBrandedImage(response.data);
      toast.success('Branded image created!');
    } catch (error) {
      console.error('Generate branded image error:', error);
      const message = error.response?.data?.detail || 'Failed to generate branded image';
      toast.error(message);
    } finally {
      setGeneratingBrandedImage(false);
    }
  };

  const handleDownloadBrandedImage = () => {
    if (!brandedImage) return;
    try {
      const link = document.createElement('a');
      link.href = brandedImage.image_url;
      link.download = brandedImage.filename || `branded_social_${listing.city.toLowerCase()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Branded image downloaded!');
    } catch (error) {
      toast.error('Failed to download image');
    }
  };

  const handleDownloadSocialImage = (image, index) => {
    try {
      const link = document.createElement('a');
      link.href = image.image_url;
      link.download = `${listing.city.toLowerCase()}_${image.platform}_${index + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Image downloaded!');
    } catch (error) {
      toast.error('Failed to download image');
    }
  };

  const handleShareSocialImage = async (image, platform) => {
    // For sharing images, we need to use the Web Share API if available
    // or fall back to copying the caption and opening the platform
    
    if (navigator.share && platform === 'general') {
      try {
        // Convert base64 to blob for sharing
        const response = await fetch(image.image_url);
        const blob = await response.blob();
        const file = new File([blob], `listing_${platform}.jpg`, { type: 'image/jpeg' });
        
        await navigator.share({
          title: `${listing.address}, ${listing.city}`,
          text: image.caption?.substring(0, 200) || '',
          files: [file]
        });
        toast.success('Shared successfully!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          toast.error('Sharing failed. Try downloading instead.');
        }
      }
    } else if (platform === 'facebook') {
      // For Facebook, open sharer with the public listing URL
      const shareUrl = listing?.share_token 
        ? `${window.location.origin}/share/${listing.share_token}`
        : window.location.href;
      const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(image.caption || '')}`;
      window.open(fbUrl, '_blank', 'width=600,height=400');
      toast.success('Opening Facebook...', {
        description: 'Download the image and upload it to your post for best results'
      });
    } else if (platform === 'instagram') {
      // For Instagram, copy caption and prompt to download
      try {
        await navigator.clipboard.writeText(image.caption || '');
        toast.success('Caption copied! Now download the image', {
          description: 'Open Instagram, create a post, upload the image, and paste the caption',
          duration: 6000
        });
      } catch (err) {
        toast.info('Download the image and copy the caption to share on Instagram');
      }
    }
  };

  const handleShareListing = async () => {
    setSharingLoading(true);
    try {
      const response = await axios.post(
        `${API}/listings/${id}/share`,
        {},
        { headers: getAuthHeaders() }
      );
      
      // Use the share_token from response but construct URL from current window location
      // This ensures the URL always matches where the user is accessing the app from
      const shareToken = response.data.share_token;
      const url = `${window.location.origin}/share/${shareToken}`;
      
      console.log('Share URL:', url);
      setShareUrl(url);
      
      // Try modern clipboard API first
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Share link copied!', {
          description: url,
          duration: 5000
        });
      } catch (clipboardError) {
        // Fallback to execCommand
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.top = '0';
        textArea.style.left = '0';
        textArea.style.width = '2em';
        textArea.style.height = '2em';
        textArea.style.padding = '0';
        textArea.style.border = 'none';
        textArea.style.outline = 'none';
        textArea.style.boxShadow = 'none';
        textArea.style.background = 'transparent';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          toast.success('Share link copied!', {
            description: url,
            duration: 5000
          });
        } else {
          // If copy fails, show the URL so user can manually copy
          toast.info('Copy this link:', {
            description: url,
            duration: 10000
          });
        }
      }
    } catch (error) {
      console.error('Share error:', error);
      toast.error('Failed to create share link');
    } finally {
      setSharingLoading(false);
    }
  };

  const handleDuplicateListing = async () => {
    setDuplicating(true);
    try {
      const response = await axios.post(
        `${API}/listings/${id}/duplicate`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success('Listing duplicated! Redirecting to the new listing...');
      
      // Navigate to the new listing
      setTimeout(() => {
        navigate(`/listings/${response.data.id}`);
      }, 1000);
    } catch (error) {
      console.error('Duplicate error:', error);
      const message = error.response?.data?.detail || 'Failed to duplicate listing';
      toast.error(message);
    } finally {
      setDuplicating(false);
    }
  };

  const getCurrentContent = () => {
    return content.find(c => c.language === selectedLang) || content[0];
  };

  // Social Media Share Functions
  const shareToFacebook = (text, imageUrl = null) => {
    // Get the public share URL if available
    const shareUrl = listing?.share_token 
      ? `${window.location.origin}/share/${listing.share_token}`
      : window.location.href;
    
    // Facebook Share Dialog
    const fbUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`;
    window.open(fbUrl, '_blank', 'width=600,height=400');
    toast.success('Opening Facebook...');
  };

  const shareToInstagram = (text) => {
    // Copy caption to clipboard - user will open Instagram manually
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Caption copied to clipboard!', {
          description: 'Now open Instagram and paste when creating your post',
          duration: 5000
        });
      } else {
        toast.error('Failed to copy. Please select and copy the text manually.');
      }
    } catch (err) {
      console.error('Copy error:', err);
      toast.error('Failed to copy. Please select and copy the text manually.');
    }
  };

  const ShareButtons = ({ text, platform }) => (
    <div className="flex gap-2">
      {(platform === 'facebook' || platform === 'both') && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => shareToFacebook(text)}
          className="gap-2 rounded-full bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border-[#1877F2]/30 text-[#1877F2]"
          data-testid={`share-facebook-${platform}`}
        >
          <Facebook size={14} />
          Share
        </Button>
      )}
      {(platform === 'instagram' || platform === 'both') && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => shareToInstagram(text)}
          className="gap-2 rounded-full bg-gradient-to-r from-[#833AB4]/10 via-[#FD1D1D]/10 to-[#F77737]/10 hover:from-[#833AB4]/20 hover:via-[#FD1D1D]/20 hover:to-[#F77737]/20 border-[#E1306C]/30 text-[#E1306C]"
          data-testid={`share-instagram-${platform}`}
        >
          <Instagram size={14} />
          Copy Caption
        </Button>
      )}
    </div>
  );

  const CopyButton = ({ text, field }) => (
    <Button
      variant="outline"
      size="sm"
      onClick={() => handleCopy(text, field)}
      className="gap-2 rounded-full"
      data-testid={`copy-${field}`}
    >
      {copiedField === field ? <Check size={14} /> : <Copy size={14} />}
      {copiedField === field ? 'Copied!' : 'Copy'}
    </Button>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <Navbar variant="app" />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <Navbar variant="app" />
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <p className="text-muted-foreground mb-4">Listing not found</p>
          <Link to="/dashboard">
            <Button variant="outline">Back to Dashboard</Button>
          </Link>
        </div>
      </div>
    );
  }

  const currentContent = getCurrentContent();

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="listing-detail">
      <Navbar variant="app" />
      
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="gap-2"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf || content.length === 0}
              className="gap-2 rounded-full"
              data-testid="download-pdf-btn"
            >
              {downloadingPdf ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />}
              {downloadingPdf ? 'Generating...' : 'Download PDF'}
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              className="gap-2 rounded-full"
              data-testid="export-btn"
            >
              <Download size={16} />
              Export JSON
            </Button>
            <Button
              variant="outline"
              onClick={handleDuplicateListing}
              disabled={duplicating}
              className="gap-2 rounded-full"
              data-testid="duplicate-listing-btn"
            >
              {duplicating ? <Loader2 size={16} className="animate-spin" /> : <CopyPlus size={16} />}
              {duplicating ? 'Duplicating...' : 'Duplicate'}
            </Button>
            <Button
              variant="default"
              onClick={handleShareListing}
              disabled={sharingLoading}
              className="gap-2 rounded-full"
              data-testid="share-listing-btn"
            >
              {sharingLoading ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
              {shareUrl ? 'Link Copied!' : 'Share Listing'}
            </Button>
          </div>
        </div>

        {/* Listing Info */}
        <Card className="mb-6 border-border/40">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-2xl font-heading font-bold text-foreground">
                    {listing.title}
                  </h1>
                  <Badge className={statusColors[listing.status]}>
                    {listing.status}
                  </Badge>
                </div>
                <p className="text-muted-foreground">
                  ${listing.price?.toLocaleString()} • {listing.bedrooms} bd • {listing.bathrooms} ba • {listing.sqft?.toLocaleString()} sqft
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Select value={listing.status} onValueChange={handleStatusChange}>
                  <SelectTrigger className="w-36" data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Draft">Draft</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Pending">Pending</SelectItem>
                    <SelectItem value="Sold">Sold</SelectItem>
                    <SelectItem value="Archived">Archived</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Photos Gallery */}
        <Card 
          className={`mb-6 border-border/40 transition-all duration-200 ${isDraggingPhotos ? 'ring-2 ring-primary border-primary' : ''}`}
          onDragEnter={handlePhotoDragEnter}
          onDragLeave={handlePhotoDragLeave}
          onDragOver={handlePhotoDragOver}
          onDrop={handlePhotoDrop}
        >
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="font-heading text-lg">
              Photos ({listing.photos?.length || 0})
              {isDraggingPhotos && <span className="text-primary ml-2">Drop files here!</span>}
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Upload Button */}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/*,.heic,.dng,.raw,.cr2,.nef"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploadingPhoto}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-full pointer-events-none"
                  disabled={uploadingPhoto}
                  data-testid="upload-photos-btn"
                  asChild
                >
                  <span>
                    {uploadingPhoto ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Plus size={14} />
                        Add Photos
                      </>
                    )}
                  </span>
                </Button>
              </label>
              
              {listing.photos && listing.photos.length > 0 && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPhotos}
                    disabled={downloadingPhotos}
                    className="gap-2 rounded-full"
                    data-testid="download-photos-btn"
                  >
                    {downloadingPhotos ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        Downloading...
                      </>
                    ) : (
                      <>
                        <ImageDown size={14} />
                        Download ZIP
                      </>
                    )}
                  </Button>
                  {listing.photos.some(p => !p.enhanced_photo_url || p.enhancement_failed) && (
                    <div className="flex items-center gap-2">
                      <select
                        value={enhancementLevel}
                        onChange={(e) => setEnhancementLevel(e.target.value)}
                        className="h-9 px-3 text-sm border border-input rounded-full bg-background hover:bg-accent cursor-pointer"
                        data-testid="enhancement-level-select"
                      >
                        <option value="subtle">✨ Subtle</option>
                        <option value="standard">⚡ Standard</option>
                        <option value="dramatic">🔥 Dramatic</option>
                      </select>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEnhanceAllPhotos}
                        disabled={enhancingAll}
                        className="gap-2 rounded-full"
                        data-testid="enhance-all-btn"
                      >
                        {enhancingAll ? (
                          <>
                            <Loader2 size={14} className="animate-spin" />
                            Enhancing...
                          </>
                        ) : (
                          <>
                            <Sparkles size={14} />
                            Enhance All
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  {listing.photos.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openBatchStagingModal}
                      className="gap-2 rounded-full bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100"
                      data-testid="virtual-staging-btn"
                    >
                      <Sofa size={14} />
                      Virtual Staging
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {/* Info message for AI enhancement */}
            {listing.photos && listing.photos.length > 0 && listing.photos.some(p => !p.enhanced_photo_url || p.enhancement_failed) && (
              <Alert className="mb-4 bg-blue-50 border-blue-200" data-testid="enhance-info-alert">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <strong>AI Photo Enhancement</strong>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleRefresh}
                      disabled={refreshing}
                      className="gap-1 text-blue-700 border-blue-300 hover:bg-blue-100"
                      data-testid="refresh-photos-btn"
                    >
                      <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''} />
                      {refreshing ? 'Refreshing...' : 'Refresh'}
                    </Button>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">✨ Subtle:</span> Light touch-ups, natural look</p>
                    <p><span className="font-medium">⚡ Standard:</span> Balanced enhancement, professional quality</p>
                    <p><span className="font-medium">🔥 Dramatic:</span> Magazine-quality, very noticeable changes</p>
                  </div>
                  {listing.photos.some(p => !p.enhanced_photo_url || p.enhanced_photo_url === p.original_photo_url) && (
                    <p className="text-xs mt-2 text-amber-600">💡 Some photos still need enhancement. Click "Enhance All" to continue.</p>
                  )}
                  <p className="text-xs mt-2 text-blue-600">Enhancement runs in the background. Refresh to check progress.</p>
                </AlertDescription>
              </Alert>
            )}
            {listing.photos && listing.photos.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={listing.photos.map(p => p.id)}
                  strategy={rectSortingStrategy}
                >
                  <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                    <GripVertical size={14} />
                    <span>Drag photos to reorder. First photo becomes your main listing image.</span>
                    {isReordering && <Loader2 size={14} className="animate-spin" />}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {listing.photos.map((photo, index) => (
                      <SortablePhoto
                        key={photo.id}
                        photo={photo}
                        index={index}
                        isFirst={index === 0}
                        onClick={openLightbox}
                        onDelete={handleDeletePhoto}
                        onStage={handleStageSinglePhoto}
                        onEnhance={handleEnhanceSinglePhoto}
                        enhancingPhotoId={enhancingPhotoId}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className={`text-center py-12 border-2 border-dashed rounded-xl transition-all ${isDraggingPhotos ? 'border-primary bg-primary/5' : 'border-border'}`}>
                <Upload size={48} className={`mx-auto mb-3 ${isDraggingPhotos ? 'text-primary animate-bounce' : 'opacity-50'}`} />
                <p className="text-muted-foreground font-medium">
                  {isDraggingPhotos ? 'Drop files here!' : 'Drag & drop photos or folders here'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  or click "Add Photos" above
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Content Section */}
        {content.length > 0 ? (
          <Card className="border-border/40">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading text-lg">Generated Content</CardTitle>
              <div className="flex items-center gap-3">
                {content.length > 1 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Language:</span>
                    <Select value={selectedLang} onValueChange={setSelectedLang}>
                      <SelectTrigger className="w-32" data-testid="language-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {content.map((c) => (
                          <SelectItem key={c.language} value={c.language}>
                            {c.language === 'EN' ? 'English' : 'Spanish'}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    setGeneratingContent(true);
                    toast.loading('Regenerating content...', { id: 'regen-content' });
                    try {
                      const response = await axios.post(
                        `${API}/listings/${id}/generate`,
                        {},
                        { headers: getAuthHeaders() }
                      );
                      toast.success('Content regenerated!', { id: 'regen-content' });
                      fetchListing();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to regenerate content', { id: 'regen-content' });
                    } finally {
                      setGeneratingContent(false);
                    }
                  }}
                  disabled={generatingContent}
                  className="gap-2"
                  data-testid="regenerate-content-btn"
                >
                  {generatingContent ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Regenerate
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="mls" className="w-full">
                <TabsList className="grid grid-cols-5 w-full mb-6">
                  <TabsTrigger value="mls" className="gap-2" data-testid="tab-mls">
                    <FileText size={14} />
                    <span className="hidden sm:inline">MLS</span>
                  </TabsTrigger>
                  <TabsTrigger value="social" className="gap-2" data-testid="tab-social">
                    <Instagram size={14} />
                    <span className="hidden sm:inline">Social</span>
                  </TabsTrigger>
                  <TabsTrigger value="email" className="gap-2" data-testid="tab-email">
                    <Mail size={14} />
                    <span className="hidden sm:inline">Email</span>
                  </TabsTrigger>
                  <TabsTrigger value="sms" className="gap-2" data-testid="tab-sms">
                    <MessageSquare size={14} />
                    <span className="hidden sm:inline">SMS</span>
                  </TabsTrigger>
                  <TabsTrigger value="updates" className="gap-2" data-testid="tab-updates">
                    <Home size={14} />
                    <span className="hidden sm:inline">Updates</span>
                  </TabsTrigger>
                </TabsList>

                {/* MLS Tab */}
                <TabsContent value="mls" className="space-y-4">
                  <div className="bg-secondary/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-muted-foreground">MLS REMARKS</span>
                      <CopyButton text={currentContent?.mls_remarks} field="mls_remarks" />
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">
                      {currentContent?.mls_remarks}
                    </p>
                  </div>
                  
                  <div className="bg-accent/5 rounded-xl p-4 border border-accent/20">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-accent">MLS-READY DATA</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCopy(
                          `Address: ${listing.address}, ${listing.city}, ${listing.state} ${listing.zip_code}\nPrice: $${listing.price?.toLocaleString()}\nBedrooms: ${listing.bedrooms}\nBathrooms: ${listing.bathrooms}\nSquare Feet: ${listing.sqft?.toLocaleString()}\nProperty Type: ${listing.property_type}\n\nRemarks:\n${currentContent?.mls_remarks}`,
                          'mls_all'
                        )}
                        className="gap-2 rounded-full bg-accent/10 border-accent/20 hover:bg-accent/20"
                        data-testid="copy-all-mls"
                      >
                        <Copy size={14} />
                        Copy All MLS Fields
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Address</span>
                        <p className="font-medium">{listing.address}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Price</span>
                        <p className="font-medium">${listing.price?.toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Beds/Baths</span>
                        <p className="font-medium">{listing.bedrooms} / {listing.bathrooms}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Sqft</span>
                        <p className="font-medium">{listing.sqft?.toLocaleString()}</p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-4">
                      ⚠️ Review for accuracy and compliance before posting/submitting to MLS.
                    </p>
                  </div>
                </TabsContent>

                {/* Social Tab */}
                <TabsContent value="social" className="space-y-4">
                  {/* Generate Social Images Section */}
                  <Card className="border-primary/20 bg-primary/5">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-4">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                          <div>
                            <h4 className="font-medium text-foreground flex items-center gap-2">
                              <Sparkles size={16} className="text-primary" />
                              Create Social Media Images
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              Select photos and generate ready-to-post images with property details
                            </p>
                          </div>
                        </div>
                        
                        {/* Photo Selection Grid */}
                        {listing?.photos?.length > 0 ? (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">
                                Select Photos ({selectedPhotosForSocial.length} selected)
                              </span>
                              <div className="flex gap-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={selectAllPhotos}
                                  className="text-xs h-7"
                                >
                                  Select All
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={deselectAllPhotos}
                                  className="text-xs h-7"
                                >
                                  Clear
                                </Button>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {listing.photos.map((photo) => (
                                <div 
                                  key={photo.id}
                                  onClick={() => togglePhotoSelection(photo.id)}
                                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                                    selectedPhotosForSocial.includes(photo.id) 
                                      ? 'border-primary ring-2 ring-primary/30' 
                                      : 'border-transparent hover:border-muted-foreground/30'
                                  }`}
                                >
                                  <img 
                                    src={photo.staged_photo_url || photo.enhanced_photo_url || photo.thumbnail_url || photo.original_photo_url}
                                    alt="Select for social"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                  {selectedPhotosForSocial.includes(photo.id) && (
                                    <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                                      <div className="bg-primary text-white rounded-full p-1">
                                        <Check size={14} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            
                            {/* Generate Buttons */}
                            <div className="flex gap-2 mt-4">
                              <Button
                                size="sm"
                                onClick={() => handleGenerateSocialImages('instagram')}
                                disabled={generatingSocialImages || selectedPhotosForSocial.length === 0}
                                className="gap-2 flex-1 sm:flex-none bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-90 text-white"
                                data-testid="generate-instagram-images-btn"
                              >
                                {generatingSocialImages ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <Instagram size={14} />
                                )}
                                Generate for Instagram
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleGenerateSocialImages('facebook')}
                                disabled={generatingSocialImages || selectedPhotosForSocial.length === 0}
                                className="gap-2 flex-1 sm:flex-none bg-[#1877F2] hover:bg-[#1877F2]/90 text-white"
                                data-testid="generate-facebook-images-btn"
                              >
                                {generatingSocialImages ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <Facebook size={14} />
                                )}
                                Generate for Facebook
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-amber-600">
                            Please upload photos first to generate social media images.
                          </p>
                        )}
                        
                        {/* Generated Images Grid */}
                        {socialImages.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <p className="text-sm font-medium text-muted-foreground mb-3">
                              Generated {socialImages[0]?.platform === 'instagram' ? 'Instagram' : 'Facebook'} Images ({socialImages.length})
                            </p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                              {socialImages.map((img, index) => (
                                <div key={img.id} className="relative group rounded-lg overflow-hidden">
                                  <img 
                                    src={img.image_url} 
                                    alt={`Social media image ${index + 1}`}
                                    className="w-full aspect-square object-cover"
                                  />
                                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2 p-2">
                                    <Button
                                      size="sm"
                                      variant="secondary"
                                      onClick={() => handleDownloadSocialImage(img, index)}
                                      className="gap-2 w-full"
                                    >
                                      <Download size={14} />
                                      Download
                                    </Button>
                                    <div className="flex gap-2 w-full">
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => handleShareSocialImage(img, 'facebook')}
                                        className="gap-1 flex-1 bg-[#1877F2] hover:bg-[#1877F2]/80 text-white"
                                      >
                                        <Facebook size={12} />
                                        Share
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={() => handleShareSocialImage(img, 'instagram')}
                                        className="gap-1 flex-1 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-80 text-white"
                                      >
                                        <Instagram size={12} />
                                        Share
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-muted-foreground mt-3">
                              💡 Tip: Download the image, then click Share to open the platform with caption ready to paste
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Branded Social Image Creator */}
                  <Card className="border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-orange-500/5">
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-4">
                        <div>
                          <h4 className="font-medium text-foreground flex items-center gap-2">
                            <User size={16} className="text-amber-600" />
                            Branded Social Media Image
                            <Badge variant="outline" className="text-xs font-normal">Optional</Badge>
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            Create a professional branded image with your headshot, broker logo, and property details
                          </p>
                        </div>
                        
                        {/* Photo Selection */}
                        {listing?.photos?.length > 0 ? (
                          <div>
                            <span className="text-sm font-medium mb-2 block">Select a Photo</span>
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                              {listing.photos.map((photo) => (
                                <div 
                                  key={photo.id}
                                  onClick={() => setSelectedPhotoForBranded(photo.id)}
                                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                                    selectedPhotoForBranded === photo.id 
                                      ? 'border-amber-500 ring-2 ring-amber-500/30' 
                                      : 'border-transparent hover:border-muted-foreground/30'
                                  }`}
                                >
                                  <img 
                                    src={photo.staged_photo_url || photo.enhanced_photo_url || photo.thumbnail_url || photo.original_photo_url}
                                    alt="Select for branded"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                  {selectedPhotoForBranded === photo.id && (
                                    <div className="absolute inset-0 bg-amber-500/20 flex items-center justify-center">
                                      <div className="bg-amber-500 text-white rounded-full p-1">
                                        <Check size={14} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                            
                            {/* Options */}
                            <div className="flex flex-wrap gap-4 mt-4">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                  type="checkbox" 
                                  checked={includeAgentPhoto}
                                  onChange={(e) => setIncludeAgentPhoto(e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Include my photo</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input 
                                  type="checkbox" 
                                  checked={includeBrokerLogo}
                                  onChange={(e) => setIncludeBrokerLogo(e.target.checked)}
                                  className="rounded border-gray-300"
                                />
                                <span className="text-sm">Include broker logo</span>
                              </label>
                            </div>
                            
                            {/* Generate Button */}
                            <Button
                              onClick={handleGenerateBrandedImage}
                              disabled={generatingBrandedImage || !selectedPhotoForBranded}
                              className="gap-2 mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                              data-testid="generate-branded-image-btn"
                            >
                              {generatingBrandedImage ? (
                                <>
                                  <Loader2 size={16} className="animate-spin" />
                                  Creating...
                                </>
                              ) : (
                                <>
                                  <Sparkles size={16} />
                                  Create Branded Image
                                </>
                              )}
                            </Button>
                            
                            {/* Missing profile warning */}
                            {(!agentProfile?.headshot_photo || !agentProfile?.brokerage_logo) && (
                              <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                                <Home size={12} />
                                {!agentProfile?.headshot_photo && !agentProfile?.brokerage_logo 
                                  ? 'Add your headshot and broker logo in your profile for best results'
                                  : !agentProfile?.headshot_photo 
                                    ? 'Add your headshot in your profile for best results'
                                    : 'Add your broker logo in your profile for best results'}
                              </p>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No photos available</p>
                        )}
                        
                        {/* Generated Branded Image Result */}
                        {brandedImage && (
                          <div className="border rounded-lg p-4 bg-background">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium">Your Branded Image</span>
                              <Button 
                                size="sm" 
                                onClick={handleDownloadBrandedImage}
                                className="gap-2"
                                data-testid="download-branded-image-btn"
                              >
                                <Download size={14} />
                                Download
                              </Button>
                            </div>
                            <div className="rounded-lg overflow-hidden max-w-md mx-auto">
                              <img 
                                src={brandedImage.image_url} 
                                alt="Branded social media" 
                                className="w-full h-auto"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground mt-3 text-center">
                              Right-click to save or use the download button above
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="bg-secondary/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Instagram size={16} />
                        INSTAGRAM CAPTION
                      </span>
                      <div className="flex gap-2">
                        <CopyButton text={currentContent?.instagram_caption} field="instagram" />
                        <ShareButtons text={currentContent?.instagram_caption} platform="instagram" />
                      </div>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">
                      {currentContent?.instagram_caption}
                    </p>
                  </div>
                  
                  <div className="bg-secondary/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                        <Facebook size={16} />
                        FACEBOOK POST
                      </span>
                      <div className="flex gap-2">
                        <CopyButton text={currentContent?.facebook_post} field="facebook" />
                        <ShareButtons text={currentContent?.facebook_post} platform="facebook" />
                      </div>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">
                      {currentContent?.facebook_post}
                    </p>
                  </div>
                </TabsContent>

                {/* Email Tab */}
                <TabsContent value="email" className="space-y-4">
                  <div className="bg-secondary/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold">Professional Email Template</h3>
                        <p className="text-sm text-muted-foreground">
                          Copy the HTML and paste it into your email editor (Gmail, Outlook, etc.)
                        </p>
                      </div>
                      <Button 
                        onClick={copyEmailHTML}
                        className="gap-2"
                        size="lg"
                        data-testid="copy-email-html-btn"
                      >
                        {copiedField === 'email_html' ? (
                          <>
                            <Check size={18} />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy size={18} />
                            Copy HTML Email
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Email Preview */}
                    <div className="border rounded-lg overflow-hidden bg-white">
                      <div className="bg-muted/50 px-4 py-2 border-b flex items-center gap-2">
                        <Mail size={16} className="text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Email Preview</span>
                      </div>
                      <div 
                        className="p-0 max-h-[500px] overflow-y-auto"
                        dangerouslySetInnerHTML={{ __html: generateEmailHTML() }}
                      />
                    </div>
                    
                    <p className="text-xs text-muted-foreground mt-3">
                      💡 Tip: Most email clients support HTML formatting. Simply paste the copied content and the design will be preserved.
                    </p>
                  </div>
                </TabsContent>

                {/* SMS Tab */}
                <TabsContent value="sms" className="space-y-4">
                  <div className="bg-secondary/30 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-muted-foreground">SMS TEXT</span>
                      <CopyButton text={currentContent?.sms_copy} field="sms" />
                    </div>
                    <p className="text-foreground">
                      {currentContent?.sms_copy}
                    </p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {currentContent?.sms_copy?.length || 0} / 160 characters
                    </p>
                  </div>
                </TabsContent>

                {/* Updates Tab */}
                <TabsContent value="updates" className="space-y-4">
                  <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-yellow-700">PENDING / UNDER CONTRACT</span>
                      <CopyButton text={currentContent?.pending_update_text} field="pending" />
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">
                      {currentContent?.pending_update_text}
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-blue-700">SOLD</span>
                      <CopyButton text={currentContent?.sold_update_text} field="sold" />
                    </div>
                    <p className="text-foreground whitespace-pre-wrap">
                      {currentContent?.sold_update_text}
                    </p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-border/40">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
                <Sparkles className="text-muted-foreground" size={32} />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No content generated yet</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-sm">
                Generate your marketing package to see MLS remarks, social posts, and more.
              </p>
              <Link to={`/listings/new?edit=${id}`}>
                <Button className="bg-accent hover:bg-accent/90 rounded-full gap-2">
                  <Sparkles size={16} />
                  Generate Content
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Calculator Section - Multiple Calculations */}
        {listing?.calculations && listing.calculations.length > 0 && (
          <Card className="border-border/40 mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calculator className="text-primary" size={20} />
                  Payment Calculators ({listing.calculations.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {listing.calculations.map((calc, index) => (
                <div key={calc.id} className="relative">
                  {/* Remove button for each calculation */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      if (confirm(`Remove this ${calc.calculator_type} calculation?`)) {
                        try {
                          await axios.post(`${API}/listings/${id}/assign-calculation`, null, {
                            params: { calculation_id: calc.id, action: 'remove' },
                            headers: getAuthHeaders()
                          });
                          setListing(prev => ({
                            ...prev,
                            calculations: prev.calculations.filter(c => c.id !== calc.id)
                          }));
                          toast.success('Calculation removed');
                        } catch (error) {
                          toast.error('Failed to remove calculation');
                        }
                      }
                    }}
                    className="absolute top-0 right-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 size={16} />
                  </Button>

                  {/* Mortgage Calculator Display */}
                  {calc.calculator_type === 'mortgage' && calc.mortgage_data && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-3 flex items-center gap-2">
                        <DollarSign size={18} />
                        Mortgage Calculator
                      </h4>
                      
                      {/* Main stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Monthly Payment</p>
                          <p className="text-xl font-bold text-primary">
                            ${fmtCurrency(calc.mortgage_data.monthly_payment)}
                          </p>
                        </div>
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Home Price</p>
                          <p className="text-lg font-semibold">
                            ${fmtCurrency(calc.mortgage_data.home_price)}
                          </p>
                        </div>
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Down Payment</p>
                          <p className="text-lg font-semibold">
                            ${fmtCurrency(calc.mortgage_data.down_payment)}
                          </p>
                          <p className="text-xs text-muted-foreground">({safeNum(calc.mortgage_data.down_payment_percent)}%)</p>
                        </div>
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Interest Rate</p>
                          <p className="text-lg font-semibold">
                            {safeNum(calc.mortgage_data.interest_rate)}%
                          </p>
                          <p className="text-xs text-muted-foreground">{safeNum(calc.mortgage_data.loan_term)} years</p>
                        </div>
                      </div>

                      {/* Detailed breakdown */}
                      <div className="bg-white dark:bg-background rounded-lg p-3 shadow-sm">
                        <p className="text-sm font-medium mb-2">Monthly Payment Breakdown</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Principal & Interest:</span>
                            <span className="font-medium">${fmtCurrency(calc.mortgage_data.monthly_pi) || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Property Tax:</span>
                            <span className="font-medium">${fmtCurrency(calc.mortgage_data.monthly_tax)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Insurance:</span>
                            <span className="font-medium">${fmtCurrency(calc.mortgage_data.monthly_insurance)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">HOA:</span>
                            <span className="font-medium">${fmtCurrency(calc.mortgage_data.monthly_hoa)}</span>
                          </div>
                        </div>
                        <div className="mt-2 pt-2 border-t flex justify-between text-sm">
                          <span className="text-muted-foreground">Loan Amount:</span>
                          <span className="font-semibold">${fmtCurrency(calc.mortgage_data.loan_amount)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Affordability Calculator Display */}
                  {calc.calculator_type === 'affordability' && calc.affordability_data && (
                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                      <h4 className="font-semibold text-green-700 dark:text-green-300 mb-3 flex items-center gap-2">
                        <Home size={18} />
                        Home Affordability Calculator
                      </h4>
                      
                      {/* Main stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Max Home Price</p>
                          <p className="text-xl font-bold text-green-600">
                            ${fmtCurrency(calc.affordability_data.max_home_price)}
                          </p>
                        </div>
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Monthly Payment</p>
                          <p className="text-lg font-semibold">
                            ${fmtCurrency(calc.affordability_data.monthly_payment)}
                          </p>
                        </div>
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">DTI Ratio</p>
                          <p className="text-lg font-semibold">
                            {fmtPct(calc.affordability_data.current_dti)}%
                          </p>
                          <p className="text-xs text-muted-foreground">of {safeNum(calc.affordability_data.max_dti) || 43}% max</p>
                        </div>
                        <div className="bg-white dark:bg-background rounded-lg p-3 text-center shadow-sm">
                          <p className="text-xs text-muted-foreground mb-1">Down Payment</p>
                          <p className="text-lg font-semibold">
                            ${fmtCurrency(calc.affordability_data.down_payment)}
                          </p>
                          <p className="text-xs text-muted-foreground">({safeNum(calc.affordability_data.down_payment_percent) || 20}%)</p>
                        </div>
                      </div>

                      {/* Detailed breakdown */}
                      <div className="bg-white dark:bg-background rounded-lg p-3 shadow-sm">
                        <p className="text-sm font-medium mb-2">Income & Debt Details</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Annual Income:</span>
                            <span className="font-medium">${fmtCurrency(calc.affordability_data.annual_income) || 'N/A'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Monthly Debts:</span>
                            <span className="font-medium">${fmtCurrency(calc.affordability_data.monthly_debts)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Interest Rate:</span>
                            <span className="font-medium">{safeNum(calc.affordability_data.interest_rate) || 'N/A'}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Loan Term:</span>
                            <span className="font-medium">{safeNum(calc.affordability_data.loan_term) || 30} years</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Loan Amount:</span>
                            <span className="font-medium">${fmtCurrency(calc.affordability_data.loan_amount) || 'N/A'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {index < listing.calculations.length - 1 && (
                    <div className="border-b border-border/40 mt-4" />
                  )}
                </div>
              ))}
              
              <p className="text-xs text-muted-foreground text-center">
                These calculations will appear on the public listing page with a disclaimer.
              </p>
            </CardContent>
          </Card>
        )}

        {/* No calculation assigned - show option to add */}
        {(!listing?.calculations || listing.calculations.length === 0) && (
          <Card className="border-dashed border-2 border-border/40 mt-6">
            <CardContent className="flex flex-col items-center justify-center py-8">
              <Calculator className="text-muted-foreground mb-3" size={32} />
              <p className="text-muted-foreground text-center mb-4">
                No payment calculator assigned to this listing
              </p>
              <div className="flex gap-2">
                <Link to="/mortgage-calculator">
                  <Button variant="outline" size="sm" className="gap-2">
                    <DollarSign size={14} />
                    Mortgage Calculator
                  </Button>
                </Link>
                <Link to="/affordability-calculator">
                  <Button variant="outline" size="sm" className="gap-2">
                    <Home size={14} />
                    Affordability Calculator
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
      
      {/* Virtual Staging Modal */}
      <Dialog open={showStagingModal} onOpenChange={setShowStagingModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sofa className="h-5 w-5 text-purple-500" />
              Virtual Staging {stagingPhotos.length > 1 && `(${stagingPhotos.length} photos)`}
            </DialogTitle>
            <DialogDescription>
              Add furniture to empty rooms. The AI will preserve the room&apos;s structure and only add furniture and decor.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Photo Selection Grid */}
            {listing?.photos && listing.photos.length > 1 && (
              <div className="space-y-2">
                <Label>Select Photos to Stage</Label>
                <div className="grid grid-cols-4 gap-2 max-h-[200px] overflow-y-auto p-2 border rounded-lg">
                  {listing.photos.map((photo) => (
                    <div 
                      key={photo.id}
                      onClick={() => togglePhotoForStaging(photo)}
                      className={`aspect-square rounded-lg overflow-hidden cursor-pointer relative border-2 transition-all ${
                        stagingPhotos.some(p => p.id === photo.id)
                          ? 'border-purple-500 ring-2 ring-purple-500/30'
                          : 'border-transparent hover:border-gray-300'
                      }`}
                    >
                      <img
                        src={photo.staged_photo_url || photo.enhanced_photo_url || photo.thumbnail_url || photo.original_photo_url}
                        alt="Select for staging"
                        className="w-full h-full object-cover"
                      />
                      {stagingPhotos.some(p => p.id === photo.id) && (
                        <div className="absolute top-1 right-1 bg-purple-500 text-white rounded-full p-0.5">
                          <Check size={12} />
                        </div>
                      )}
                      {photo.staged_photo_url && (
                        <div className="absolute bottom-1 left-1 bg-purple-500 text-white text-[10px] px-1 rounded">
                          Staged
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stagingPhotos.length} photo{stagingPhotos.length !== 1 ? 's' : ''} selected
                </p>
              </div>
            )}
            
            {/* Preview of selected photo(s) */}
            {stagingPhotos.length === 1 && (
              <div className="aspect-video rounded-lg overflow-hidden bg-muted">
                <img
                  src={stagingPhotos[0].staged_photo_url || stagingPhotos[0].enhanced_photo_url || stagingPhotos[0].thumbnail_url || stagingPhotos[0].original_photo_url}
                  alt="Photo to stage"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            
            {/* Staging Type */}
            <div className="space-y-2">
              <Label>Staging Type</Label>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant={stagingType === 'furniture' ? 'default' : 'outline'}
                  onClick={() => setStagingType('furniture')}
                  className="justify-start gap-2"
                >
                  <Sofa className="h-4 w-4" />
                  Add Furniture
                </Button>
                <Button
                  variant={stagingType === 'style' ? 'default' : 'outline'}
                  onClick={() => setStagingType('style')}
                  className="justify-start gap-2"
                >
                  <Palette className="h-4 w-4" />
                  Change Style
                </Button>
              </div>
            </div>
            
            {/* Same Room, Different Angles Option - only show when multiple photos selected */}
            {stagingPhotos.length >= 2 && stagingType === 'furniture' && (
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consistentRoomMode}
                    onChange={(e) => setConsistentRoomMode(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <span className="font-medium text-purple-700 dark:text-purple-300">
                      Same Room, Different Angles
                    </span>
                    <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                      Enable this if the selected photos show the same room from different angles. 
                      The AI will add the <strong>same furniture</strong> consistently across all photos.
                    </p>
                  </div>
                </label>
              </div>
            )}
            
            {/* Room Type */}
            <div className="space-y-2">
              <Label>Room Type</Label>
              <Select value={stagingRoom} onValueChange={setStagingRoom}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="living_room">Living Room</SelectItem>
                  <SelectItem value="bedroom">Bedroom</SelectItem>
                  <SelectItem value="kitchen">Kitchen</SelectItem>
                  <SelectItem value="dining_room">Dining Room</SelectItem>
                  <SelectItem value="bathroom">Bathroom</SelectItem>
                  <SelectItem value="office">Home Office</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Style Selection (only for style change) */}
            {stagingType === 'style' && (
              <div className="space-y-2">
                <Label>Design Style</Label>
                <Select value={stagingStyle} onValueChange={setStagingStyle}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="modern">Modern</SelectItem>
                    <SelectItem value="contemporary">Contemporary</SelectItem>
                    <SelectItem value="traditional">Traditional</SelectItem>
                    <SelectItem value="minimalist">Minimalist</SelectItem>
                    <SelectItem value="coastal">Coastal</SelectItem>
                    <SelectItem value="farmhouse">Farmhouse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Progress indicator for batch staging */}
            {stagingInProgress && stagingProgress.total > 1 && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Processing photos...</span>
                  <span>{stagingProgress.current} of {stagingProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${(stagingProgress.current / stagingProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStagingModal(false)} disabled={stagingInProgress}>
              Cancel
            </Button>
            <Button 
              onClick={handleVirtualStage} 
              disabled={stagingInProgress || stagingPhotos.length === 0}
              className="bg-purple-600 hover:bg-purple-700 gap-2"
            >
              {stagingInProgress ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {stagingProgress.total > 1 ? `Staging ${stagingProgress.current}/${stagingProgress.total}...` : 'Staging...'}
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Stage {stagingPhotos.length > 1 ? `${stagingPhotos.length} Photos` : 'Photo'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Photo Lightbox Modal */}
      <Dialog open={showLightbox} onOpenChange={setShowLightbox}>
        <DialogContent className="!w-auto !max-w-[95vw] !max-h-[95vh] !p-2 overflow-hidden bg-black/95 border-gray-800 [&>button]:hidden">
          <div className="relative flex items-center justify-center">
            {/* Close button */}
            <button 
              onClick={() => setShowLightbox(false)}
              className="absolute top-2 right-2 z-10 bg-white/20 hover:bg-white/30 text-white p-1.5 rounded-full transition-colors"
            >
              <X size={18} />
            </button>
            
            {/* Navigation buttons */}
            {listing?.photos && listing.photos.length > 1 && (
              <>
                <button
                  onClick={() => navigateLightbox('prev')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={() => navigateLightbox('next')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
            
            {/* Main image - prioritize staged > enhanced > original for best quality */}
            {lightboxPhoto && (
              <img
                src={lightboxPhoto.staged_photo_url || lightboxPhoto.enhanced_photo_url || lightboxPhoto.original_photo_url || lightboxPhoto.display_url || lightboxPhoto.thumbnail_url}
                alt="Full size photo"
                className="max-w-[90vw] max-h-[80vh] w-auto h-auto rounded"
                style={{ objectFit: 'contain' }}
                onError={(e) => {
                  console.error('Lightbox image failed to load:', lightboxPhoto);
                  // Try thumbnail as last resort
                  if (lightboxPhoto.thumbnail_url && e.target.src !== lightboxPhoto.thumbnail_url) {
                    e.target.src = lightboxPhoto.thumbnail_url;
                  }
                }}
              />
            )}
          </div>
          
          {/* Photo info footer - outside the image container */}
          {lightboxPhoto && (
            <div className="flex items-center justify-between text-white mt-2 px-1">
              <div className="flex items-center gap-2">
                {lightboxPhoto.staged_photo_url && (
                  <Badge className="bg-purple-500 text-xs">
                    <Sofa size={10} className="mr-1" /> Staged
                  </Badge>
                )}
                {lightboxPhoto.enhanced_photo_url && !lightboxPhoto.staged_photo_url && (
                  <Badge className="bg-green-500 text-xs">
                    <Sparkles size={10} className="mr-1" /> Enhanced
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {!lightboxPhoto.staged_photo_url && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowLightbox(false);
                      openStagingModal(lightboxPhoto);
                    }}
                    className="gap-1 bg-purple-600 border-purple-600 text-white hover:bg-purple-700 text-xs py-1 h-7"
                  >
                    <Sofa size={12} />
                    Stage
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ListingDetail;
